import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import {
  DEFAULT_COMPANY_PROFILE,
  mergeCompanyProfile,
  type CompanyProfile,
} from '@/lib/companyProfile';

/**
 * Charge et enregistre le profil d'entreprise / paramètres de facturation
 * du marchand connecté. Créé à la volée à la première sauvegarde.
 */
export function useCompanyProfile() {
  const { user } = useAuth();
  const merchantId = user?.id ?? null;
  const [profile, setProfile] = useState<CompanyProfile>(
    mergeCompanyProfile(null, merchantId ?? 0)
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!merchantId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data, error: err } = await supabase
        .from('company_profile')
        .select('*')
        .eq('merchant_id', merchantId)
        .maybeSingle();
      if (err) throw err;
      setProfile(mergeCompanyProfile(data as Partial<CompanyProfile> | null, merchantId));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur de chargement du profil');
      setProfile(mergeCompanyProfile(null, merchantId));
    } finally {
      setLoading(false);
    }
  }, [merchantId]);

  useEffect(() => {
    load();
  }, [load]);

  const save = useCallback(
    async (patch: Partial<CompanyProfile>) => {
      if (!merchantId) throw new Error('Non connecté');
      const next = { ...profile, ...patch, merchant_id: merchantId };
      const { id, ...rest } = next;
      const payload = { ...rest, updated_at: new Date().toISOString() };
      const { data, error: err } = await supabase
        .from('company_profile')
        .upsert(payload, { onConflict: 'merchant_id' })
        .select('*')
        .single();
      if (err) throw err;
      setProfile(mergeCompanyProfile(data as Partial<CompanyProfile>, merchantId));
      return data as CompanyProfile;
    },
    [merchantId, profile]
  );

  /** Réserve le prochain numéro de facture et incrémente la séquence. */
  const reserveInvoiceNumber = useCallback(
    async (build: (p: CompanyProfile, seq: number) => string): Promise<{ number: string; seq: number }> => {
      const seq = profile.next_invoice_seq || 1;
      const number = build(profile, seq);
      if (merchantId) {
        try {
          await supabase
            .from('company_profile')
            .upsert(
              { ...profile, merchant_id: merchantId, next_invoice_seq: seq + 1, updated_at: new Date().toISOString() },
              { onConflict: 'merchant_id' }
            );
        } catch {
          // la numérotation avancera à la prochaine sauvegarde du profil
        }
      }
      setProfile((prev) => ({ ...prev, next_invoice_seq: seq + 1 }));
      return { number, seq };
    },
    [profile, merchantId]
  );

  return { profile, merchantId, loading, error, reload: load, save, reserveInvoiceNumber, DEFAULT_COMPANY_PROFILE };
}

export default useCompanyProfile;